import { AfterViewInit, Component, Input, OnChanges, OnDestroy, signal } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { catchError, map } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { Fluid, FluidType } from 'src/app/perical/models/fluid.model';
import { CementingOperation, MudPit, OperationType } from 'src/app/perical/models/thermal-operation.model';
import { GetShortLengthValueFromInches } from 'src/app/perivis/shared/helpers/units.helper';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { TubularString } from 'src/app/wellbore-inputs/models/well-schematic.model';
import { DrillstringService } from 'src/app/wellbore-inputs/services/drillstring.service';

export interface CementingOperationForm {
  type: OperationType;
  drillingFluid: Fluid;
  slurryPumpRate: number;
  slurryInletTemperature: number;
  leadSlurry: Fluid;
  slurry: Fluid;
  length: number;
  fluid: Fluid;
  inletTemperature: number;
  displacementRate: number;
  cementSetTime: number;
  pipeRoughness: number;
  includeRiser: boolean;
  outsideDiameter: number;
  insideDiameter: number;
  useMudPits: boolean;
  mudPits: any;
  annulusIndex: number;
}

@Component({
    selector: 'app-cementing-operation-details',
    templateUrl: './cementing-operation-details.component.html',
    providers: subformComponentProviders(CementingOperationDetailsComponent),
    standalone: false
})
export class CementingOperationDetailsComponent implements OnChanges, OnDestroy, AfterViewInit {

  constructor(
    private _drillstringService: DrillstringService,
    private _signalRService: SignalRService
  ) { }

  public slurries: Fluid[];
  public fluids: Fluid[];
  @Input('fluids') set _fluids(fluids: Fluid[] | null | undefined) {
    this.fluids = fluids?.filter(f => f.state.type === FluidType.BRINE ||
      f.state.type === FluidType.STANDARDMUD ||
      f.state.type === FluidType.ADVANCEDMUD)
      .sort((a, b) => a.state.name.localeCompare(b.state.name));
    this.slurries = fluids?.filter(f => f.state.type === FluidType.CEMENTSLURRY).sort((a, b) => a.state.name.localeCompare(b.state.name));
  }

  @Input()
  public annularFluidId: string;

  @Input()
  public isSubsea: boolean;

  @Input()
  public surfaceTemperature: number;

  @Input()
  public isReadOnly: boolean;

  @Input()
  public userUnits: UserUnitsModel;

  @Input()
  public currentString: TubularString;

  @Input()
  public currentTubularIndex: number;

  @Input()
  public defaultRiser: any;

  @Input()
  public previousOperationMudPits: MudPit;

  public FluidType: typeof FluidType = FluidType;
  public isLoading = signal<boolean>(false);
  public selectedDrillPipe: string;
  public drillPipe: any;
  public drillpipeMissing: boolean;
  public showDrillPipeData: boolean;
  public showRiser: boolean;
  public cementLength: number;
  public tempUnit: string;
  public standardFluidFlowUnit: string;
  public slurryFlowUnit: string;
  public pipeRoughnessValidation: { min: number, max: number };
  public temperatureValidation: { min: number, max: number };
  public displacementRateValidation: { min: number, max: number };
  public slurryFlowRateValidation: { min: number, max: number };
  public useMudPits = false;
  public shortLengthUnit: string;
  public pipeRoughnessUnit: string;
  public weightUnit: string;
  public longLengthUnit: string;
  
  private _defaultPipeRoughness: number;
  private _dataBackup: any | null = null;

  public form = createForm<CementingOperation, CementingOperationForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(OperationType.CEMENTING),
      drillingFluid: new UntypedFormControl(null, [Validators.required]),
      slurryPumpRate: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0), Validators.max(1000)] }),
      slurryInletTemperature: new UntypedFormControl(null, { updateOn: 'blur' }),
      leadSlurry: new UntypedFormControl(null, [Validators.required]),
      slurry: new UntypedFormControl(null, [Validators.required]),
      length: new UntypedFormControl(0, { updateOn: 'blur', validators: [Validators.required] }),
      fluid: new UntypedFormControl(null, [Validators.required]),
      inletTemperature: new UntypedFormControl(null, { updateOn: 'blur' }),
      displacementRate: new UntypedFormControl(null, { updateOn: 'blur' }),
      cementSetTime: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0), Validators.max(1000)] }),
      pipeRoughness: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      includeRiser: new UntypedFormControl(null),
      outsideDiameter: new UntypedFormControl(null, { updateOn: 'blur' }),
      insideDiameter: new UntypedFormControl(null, { updateOn: 'blur' }),
      useMudPits: new UntypedFormControl(false),
      mudPits: new UntypedFormControl(null),
      annulusIndex: new UntypedFormControl(null)
    },
    toFormGroup: (obj: CementingOperation | null): CementingOperationForm | null => {
      if (!obj) {
        return null;
      }

      this._dataBackup = { ...obj };

      this.getData(false);

      const { slurryPumpRate, slurryInletTemperature, tailCement, cementDisplacement,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        displacementRate, cementSetTime, pipeRoughness, riser, includeRiser, useMudPits, mudPits } = obj;

      obj.drillingFluid = this.fluids?.find(f => f.id === obj.drillingFluid.id);
      obj.leadSlurry = this.slurries?.find(f => f.id === obj.leadSlurry.id);
      obj.tailCement.slurry = this.slurries?.find(f => f.id === obj.tailCement.slurry.id);
      obj.cementDisplacement.fluid = this.fluids?.find(f => f.id === obj.cementDisplacement.fluid.id);
      this.useMudPits = obj.useMudPits;
      this.showRiser = includeRiser;

      return {
        type: obj.type,
        drillingFluid: obj.drillingFluid,
        slurryPumpRate,
        slurryInletTemperature,
        leadSlurry: obj.leadSlurry,
        slurry: obj.tailCement.slurry ?? null,
        length: tailCement.length,
        fluid: obj.cementDisplacement.fluid ?? null,
        inletTemperature: cementDisplacement.inletTemperature,
        displacementRate,
        cementSetTime,
        pipeRoughness,
        includeRiser: includeRiser,
        outsideDiameter: riser?.outsideDiameter,
        insideDiameter: riser?.insideDiameter,
        useMudPits: useMudPits,
        mudPits: useMudPits ? mudPits : null,
        annulusIndex: obj.annulusIndex
      };
    },
    fromFormGroup: (formValue: CementingOperationForm): CementingOperation | null => {
      const controls = this.form.formGroup.controls;
      const check = (control, field) => control?.invalid || (control?.touched && this._dataBackup?.[field] === formValue[field]);

      if (check(controls.slurryPumpRate, 'slurryPumpRate') ||
          check(controls.slurryInletTemperature, 'slurryInletTemperature') ||
          check(controls.length, 'length') ||
          check(controls.inletTemperature, 'inletTemperature') ||
          check(controls.displacementRate, 'displacementRate') ||
          check(controls.cementSetTime, 'cementSetTime') ||
          check(controls.pipeRoughness, 'pipeRoughness') ||
          check(controls.outsideDiameter, 'outsideDiameter') ||
          check(controls.insideDiameter, 'insideDiameter')) {
        
        controls.slurryPumpRate.markAsUntouched();
        controls.slurryInletTemperature.markAsUntouched();
        controls.length.markAsUntouched();
        controls.inletTemperature.markAsUntouched();
        controls.displacementRate.markAsUntouched();
        controls.cementSetTime.markAsUntouched();
        controls.pipeRoughness.markAsUntouched();
        controls.outsideDiameter.markAsUntouched();
        controls.insideDiameter.markAsUntouched();
        return;
      }

      this._dataBackup = formValue;

      const { drillingFluid, slurryPumpRate, slurryInletTemperature, leadSlurry, slurry, length, fluid, inletTemperature, useMudPits, mudPits,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        displacementRate, cementSetTime, pipeRoughness, outsideDiameter, insideDiameter, annulusIndex, includeRiser } = formValue;

        this.form.formGroup.controls.inletTemperature.setValue((this.form.formGroup.value.inletTemperature ?? this.surfaceTemperature), { emitEvent: false });

      return {
        type: OperationType.CEMENTING,
        drillingFluid,
        slurryPumpRate,
        slurryInletTemperature,
        leadSlurry,
        tailCement: {
          slurry,
          length
        },
        cementDisplacement: {
          fluid,
          inletTemperature
        },
        displacementRate,
        cementSetTime,
        pipeRoughness,
        includeRiser,
        riser: this.showRiser ? {
          outsideDiameter,
          insideDiameter
        } : null,
        useMudPits,
        mudPits,
        annulusIndex
      };
    }
  });

  ngOnChanges(): void {
    this.isLoading.set(true);
    this.tempUnit = this.userUnits.temperature;
    this.slurryFlowUnit =this.userUnits.slurryFlow;
    this.shortLengthUnit = this.userUnits.shortLengths == 'in' ? '"' : this.userUnits.shortLengths;
    this.pipeRoughnessUnit = this.userUnits.shortLengths;
    this.weightUnit = unitsLib[this.userUnits.linearDensity].symbol;
    this.longLengthUnit = this.userUnits.longLengths;

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    this._defaultPipeRoughness = 0.001968;
    if (this.userUnits.shortLengths == 'in') {
      this.pipeRoughnessValidation = { min: 0, max: 0.25 };
    } else if (this.userUnits.shortLengths == 'cm') {
      this.pipeRoughnessValidation = { min: 0, max: 0.6 };
      this._defaultPipeRoughness = 0.00499;
    } else {
      this.pipeRoughnessValidation = { min: 0, max: 6.3 };
      this._defaultPipeRoughness = 0.0499;
    }

    switch (this.tempUnit) {
      case '°F':
        this.temperatureValidation = { min: 0, max: 500 };
        break;
      case '°C':
        this.temperatureValidation = { min: -17.77, max: 260 };
        break;
      case 'K':
        this.temperatureValidation = { min: 255.37, max: 533.15 };
        break;
    }

    switch (this.slurryFlowUnit) {
      case 'bbl/min':
        this.slurryFlowRateValidation = { min: 0, max: 1000 };
        break;
      case 'm³/d':
        this.slurryFlowRateValidation = { min: 0, max: 228942 };
        break;
      case 'm³/min':
        this.slurryFlowRateValidation = { min: 0, max: 158.9 };
        break;
    }

    this.standardFluidFlowUnit = unitsLib[this.userUnits.standardFluidFlow].symbol;
    switch (unitsLib[this.userUnits.standardFluidFlow].symbol) {
      case 'gpm':
        this.displacementRateValidation = { min: 0.1, max: 1500 };
        break;

      case 'm³/min':
        this.displacementRateValidation = { min: 0.0003, max: 5.7 }; 
        break;

      case 'L/min':
        this.displacementRateValidation = { min: 0.37, max: 5678 }; 
        break;

      case 'bpm':
        this.displacementRateValidation = { min: 0.002, max: 35.7 }; 
        break;
    }

    this.form.formGroup.controls.displacementRate.setValidators([Validators.required, Validators.min(this.displacementRateValidation.min), Validators.max(this.displacementRateValidation.max)]);
    this.form.formGroup.controls.inletTemperature.setValidators([Validators.required, Validators.min(this.temperatureValidation.min), Validators.max(this.temperatureValidation.max)]);
    this.form.formGroup.controls.slurryInletTemperature.setValidators([Validators.required, Validators.min(this.temperatureValidation.min), Validators.max(this.temperatureValidation.max)]);
    this.form.formGroup.controls.slurryPumpRate.setValidators([Validators.required, Validators.min(this.slurryFlowRateValidation.min), Validators.max(this.slurryFlowRateValidation.max)]);

    this.getData(false);
  }

  signalRfunc(data: any) {
    if (data.action === PeriforOnChangeMessages.REFRESH_DRILL_STRING) {
      this.getData(true);
    }
  }

  ngAfterViewInit() {
    if (this.form.formGroup.controls.pipeRoughness.value == null) {
      this.form.formGroup.controls.pipeRoughness.patchValue(this._defaultPipeRoughness, { emitEvent: false });
    }
    
    const defaultFluid = this.fluids ? this.fluids.find(x => x.id === this.annularFluidId) : null;
    const controls = this.form.formGroup.controls;
    if (!controls.drillingFluid.value) { this.form.formGroup.controls.drillingFluid.setValue(defaultFluid, { emitEvent: false }); }
    if (!controls.fluid.value) { this.form.formGroup.controls.fluid.setValue(defaultFluid, { emitEvent: false }); }
    if (!controls.slurry.value) { this.form.formGroup.controls.slurry.setValue(this.slurries[0], { emitEvent: false }); }
    if (!controls.leadSlurry.value) { this.form.formGroup.controls.leadSlurry.setValue(this.slurries[0], { emitEvent: false }); }

    if (this.defaultRiser.includeRiser && this.form.formGroup.get('includeRiser').value == null) {
      this.form.formGroup.get('includeRiser').setValue(true, { emitEvent: false });
      this.showRiser = true;
      this.checkRiserValues();
    }
  }

  private getData(calledFromSignalR: boolean) {
    this.isLoading.set(true);
    this._drillstringService.getDrillstringForTubular().pipe(
      map((drillstring) => {
        this.drillPipe = drillstring['drillPipe'];
        this.drillpipeMissing = !this.drillPipe;

        this.showDrillPipeData = this.isSubsea || this.currentString.type == 'Liner' ? true : null;

        const hasRiserDefault = this.isSubsea && this.currentTubularIndex > 0 ? true : false;
        const riserControl = this.form.formGroup.controls.includeRiser;
        if (riserControl.value === null) {
          riserControl.setValue(hasRiserDefault, { emitEvent: false });
          this.hasRiserChange();
        }

        this.selectedDrillPipe = this.drillPipe ?
          `Landing String: OD: ${this.drillPipe.outsideDiameter.toFixed(2)}${this.shortLengthUnit}, Weight: ${this.drillPipe.weightPerFoot.toFixed(2)}${this.weightUnit}, ID: ${this.drillPipe.insideDiameter.toFixed(2)}${this.shortLengthUnit}, Grade: ${drillstring.drillPipeGrade?.name || 'Steel'}` :
          'Please Select a Landing String';

        if (calledFromSignalR) {
          return;
        }

        if (this.form.formGroup.controls.length.value > 0) {
          this.onTailTrue();
        } else {
          this.onTailFalse();
        }

        this.cementLength = +(this.currentString.shoeMd - this.currentString.topOfCementMd).toFixed(2);
        this.form.formGroup.controls.length.setValidators([Validators.required, Validators.min(0), Validators.max(this.cementLength)]);

        this.form.formGroup.controls.inletTemperature
          .setValue((this.form.formGroup.value.inletTemperature ?? this.surfaceTemperature), { emitEvent: false });
        this.form.formGroup.controls.slurryInletTemperature
          .setValue((this.form.formGroup.value.slurryInletTemperature ?? this.surfaceTemperature), { emitEvent: false });
        // this.formGroupControls.leadSpacerInletTemperature
        //   .setValue((this.formGroupValues.leadSpacerInletTemperature ?? res.udt.surfaceAmbientTemperature), {emitEvent: false});
        // this.formGroupControls.displacementInletTemperature
        //   .setValue((this.formGroupValues.displacementInletTemperature ?? res.udt.surfaceAmbientTemperature), {emitEvent: false});
        this.isLoading.set(false);
      }),
      catchError(err => {
        this.isLoading.set(false);
        return err;
      })).subscribe();
  }

  public onTailChange(e) {
    if (e.srcElement.checked == true) {
      this.onTailTrue();
    } else {
      this.onTailFalse();
    }
  }

  onTailTrue() {
    if (!this.form.formGroup.controls.length.value) {
      this.form.formGroup.controls.length.setValue(this.userUnits.longLengths === 'ft' ? 100 : 30);
    }
  }

  onTailFalse() {
    if (this.form.formGroup.controls.length.value > 0) {
      this.form.formGroup.controls.length.setValue(0);
    }
  }

  public hasRiserChange() {
    this.showRiser = this.form.formGroup.controls.includeRiser.value ?? false;

    if (this.showRiser) {
      this.form.formGroup.controls.outsideDiameter.setValidators([Validators.required]);
      this.form.formGroup.controls.insideDiameter.setValidators([Validators.required]);
    } else {
      this.form.formGroup.controls.outsideDiameter.clearValidators();
      this.form.formGroup.controls.insideDiameter.clearValidators();
    }

    this.form.formGroup.controls.outsideDiameter.updateValueAndValidity();
    this.form.formGroup.controls.insideDiameter.updateValueAndValidity();

    this.checkRiserValues();
  }

  private checkRiserValues() {
    this.form.formGroup.controls.outsideDiameter
      .setValue((this.form.formGroup.value.outsideDiameter ?? this.defaultRiser?.riser?.outsideDiameter ?? GetShortLengthValueFromInches(21, this.userUnits.shortLengths)), { emitEvent: false });

    this.form.formGroup.controls.insideDiameter
      .setValue((this.form.formGroup.value.insideDiameter ?? this.defaultRiser?.riser?.insideDiameter ?? GetShortLengthValueFromInches(19.75, this.userUnits.shortLengths)));
  }

  ngOnDestroy(): void {
    this.signalRfunc = null;
  }
}
